import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, ShieldCheck, DoorOpen } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { BrandMark } from "@/components/layout/Ambient";
import { api, errMsg, fmtDate, fmtSize } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-[180px_1fr] gap-4 py-2 border-b border-slate-200 text-sm">
    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{label}</div>
    <div className="text-slate-900 whitespace-pre-wrap break-words" data-testid={testid}>{children || "—"}</div>
  </div>
);

export default function Certificate() {
  const { id } = useParams();
  const { meta } = useAuth();
  const b = useBranding();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/records/${id}/certificate`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [id]);
  if (err) return <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-700" data-testid="certificate-error">{err}</div>;
  if (!d) return <div className="h-40 animate-pulse rounded-lg bg-slate-100" />;
  const r = d.record;
  const typeDef = meta.record_types[r.record_type];
  return (
    <div>
      <div className="flex gap-2 mb-6 print:hidden">
        <Button variant="outline" asChild data-testid="certificate-back"><Link to={`/records/${id}`}><ArrowLeft className="w-4 h-4 mr-1.5" />Back to record</Link></Button>
        <Button onClick={() => window.print()} data-testid="certificate-print-button"><Printer className="w-4 h-4 mr-1.5" />Print / save as PDF</Button>
      </div>
      <article className="force-light bg-white text-slate-900 max-w-[820px] mx-auto border border-slate-300 shadow-sm p-10 print:shadow-none print:border-0 print:p-0 print:max-w-none" data-testid="certificate-sheet">
        <header className="flex items-start justify-between border-b-4 border-[#0A1633] pb-5">
          <div className="flex items-center gap-3">
            <BrandMark size="w-12 h-12" />
            <div><div className="font-display font-extrabold text-xl tracking-tight" data-testid="certificate-org-name">{b.name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{b.tagline}</div></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Approval cover sheet</div>
            <div className="font-mono font-bold text-2xl mt-1" data-testid="certificate-ref">{r.ref_no}</div>
            <div className="text-xs text-slate-500 font-mono">Revision {r.revision}</div>
          </div>
        </header>
        <div className="mt-5 flex items-center gap-2 rounded border border-[#C9A227] bg-[#FBF6E6] px-4 py-2.5" data-testid="certificate-office">
          <DoorOpen className="w-5 h-5 text-[#8E6B12]" />
          <div><div className="text-[10px] uppercase tracking-[0.25em] text-[#8E6B12]">Issuing office</div>
            <div className="font-display font-bold text-lg text-slate-900" data-testid="certificate-office-name">{d.office_name}</div></div>
        </div>
        <div className="mt-4 flex items-center gap-3 rounded border-2 border-emerald-700 px-4 py-3 text-emerald-800">
          <ShieldCheck className="w-6 h-6" />
          <div><div className="font-display font-bold tracking-tight">APPROVED</div>
            <div className="text-xs">{fmtDate(r.approved_at)}{r.status === "archived" && " · now archived"}</div></div>
        </div>
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-slate-900" data-testid="certificate-title">{r.title}</h1>
        <div className="text-sm text-slate-500">{typeDef.label}</div>
        <section className="mt-6">
          <Row label="Registered by">{r.created_by_name} · {fmtDate(r.created_at)}</Row>
          <Row label="Submitted">{fmtDate(r.submitted_at)}</Row>
          <Row label="Approved by" testid="certificate-approver">{r.approved_by_name}{r.approved_by_role && ` (${meta.roles[r.approved_by_role]})`}{d.approver_email && ` · ${d.approver_email}`}</Row>
          {r.decision_comment && <Row label="Approval note">{r.decision_comment}</Row>}
          {typeDef.fields.map((f) => <Row key={f.key} label={f.label}>{r.fields[f.key] !== undefined ? String(r.fields[f.key]) : ""}</Row>)}
          {r.summary && <Row label="Summary">{r.summary}</Row>}
        </section>
        <section className="mt-6">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Attached documents</div>
          {d.attachments.length === 0 ? <p className="text-sm text-slate-500">None</p> : (
            <table className="w-full text-xs" data-testid="certificate-attachments">
              <tbody>{d.attachments.map((a, i) => (
                <tr key={i} className="border-b border-slate-200 align-top">
                  <td className="py-1.5 pr-3">{a.label} <span className="text-slate-500">({a.filename}, v{a.file_version}, {fmtSize(a.size)})</span></td>
                  <td className="py-1.5 font-mono text-[10px] text-slate-600 break-all">SHA-256 {a.sha256}</td>
                </tr>))}</tbody>
            </table>
          )}
        </section>
        <section className="mt-6 rounded bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-800">Verification</div>
          {d.approval_log ? <div data-testid="certificate-audit-hash">Audit entry #{d.approval_log.seq} · <span className="font-mono break-all">{d.approval_log.hash}</span></div>
            : <div>Approval audit entry not found.</div>}
          <div>Compare this fingerprint with the Audit Logs page to confirm the approval has not been altered.</div>
        </section>
        <section className="mt-10 grid grid-cols-2 gap-10 text-xs text-slate-500">
          <div><div className="border-b border-slate-400 h-10" /><div className="mt-1">Signature</div></div>
          <div><div className="border-b border-slate-400 h-10" /><div className="mt-1">Date / official stamp</div></div>
        </section>
        <footer className="mt-8 pt-3 border-t border-slate-200 text-[10px] text-slate-400 flex justify-between">
          <span>Generated {fmtDate(d.generated_at)} by {d.generated_by} ({d.generated_by_role})</span>
          <span>Confidential — {d.office_name}</span>
        </footer>
      </article>
    </div>
  );
}
